import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
  Flex,
  IconButton,
  Tooltip,
  useDisclosure,
} from '@chakra-ui/react';
import { synchronizationAction } from 'actions';
import { updateCurrentConnection } from 'actions/synchronization';
import { CONNECTION_STATUS } from 'helpers/constant';
import { useAppDispatch } from 'hooks';
import { CircleCheck, RefreshCw, Trash2 } from 'lucide-react';
import { SharepointConnection, useDeleteConnection } from 'queries/useConnection';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

function ConnectionActionButtons({ info }: { info: SharepointConnection }) {
  const { t } = useTranslation();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = useRef<any>();
  const handleConfirmConsent = async (consentLink?: string) => {
    if (consentLink) {
      window.location.href = consentLink;
    }
  };
  const dispatch = useAppDispatch();

  const { mutate: deleteConnection, isPending: isDeleting } = useDeleteConnection();
  const handleDelete = async (name: string) => {
    deleteConnection(name);
  };

  const isConnected = info.status_code === CONNECTION_STATUS.CONNECTED;

  return (
    // Card footer — single contextual primary action + ghost trash icon.
    // Connected → "Sync now" (navigates to /sync-projects, same dispatch wiring).
    // Needs auth → "Reconnect" (filled, fires the confirm/consent handler).
    <Flex align='center' gap={2} w='100%'>
      {isConnected ? (
        // Sync now — primary action; preserves dispatch wiring verbatim
        <Tooltip label={t('connection.syncNow', { defaultValue: 'Sync now' })}>
          <Link
            to={`/sync-projects`}
            onClick={() => {
              dispatch(updateCurrentConnection(info.id));
              dispatch(synchronizationAction.resetProject());
            }}
            style={{ flex: '0 0 auto', minWidth: 0 }}
          >
            <Button
              aria-label={t('connection.syncNow', { defaultValue: 'Sync now' })}
              isDisabled={isDeleting}
              leftIcon={<RefreshCw size={15} />}
              size='ds-sm'
              variant='ds-outline'
            >
              {t('connection.syncNow', { defaultValue: 'Sync now' })}
            </Button>
          </Link>
        </Tooltip>
      ) : (
        // Reconnect — filled primary; same consent/confirm handler as before
        <Tooltip label={t('connection.reconnect', { defaultValue: 'Reconnect' })}>
          <Button
            aria-label={t('connection.reconnect', { defaultValue: 'Reconnect' })}
            isDisabled={isDeleting}
            leftIcon={<CircleCheck size={15} />}
            onClick={() => handleConfirmConsent(info.consent_link)}
            size='ds-sm'
            variant='ds-soft'
          >
            {t('connection.reconnect', { defaultValue: 'Reconnect' })}
          </Button>
        </Tooltip>
      )}

      {/* Delete — ghost trash icon (no red box); confirm dialog wiring unchanged */}
      <Tooltip label={t('connection.deleteConnection')}>
        <IconButton
          flexShrink={0}
          ml='auto'
          color='text.muted'
          aria-label={t('connection.deleteConnection')}
          icon={<Trash2 size={15} />}
          isLoading={isDeleting}
          onClick={() => onOpen()}
          size='ds-sm'
          variant='ds-ghost'
        />
      </Tooltip>

      <AlertDialog
        isOpen={isOpen}
        leastDestructiveRef={cancelRef}
        onClose={onClose}
        returnFocusOnClose={false}
      >
        <AlertDialogOverlay bg='blackAlpha.600' backdropFilter='blur(3px)'>
          <AlertDialogContent
            pos='relative'
            bg='bg.surfaceRaised'
            borderWidth='1px'
            borderColor='border.default'
            borderRadius='xl'
            shadow='ds_lg'
          >
            <AlertDialogHeader
              color='text.default'
              fontFamily='heading'
              fontSize='lg'
              fontWeight={600}
            >
              {t('connection.deleteConnection')}
            </AlertDialogHeader>

            <AlertDialogBody color='text.muted' fontSize='sm'>
              {t('connection.deleteConnectionConfirmation')}
            </AlertDialogBody>

            <AlertDialogFooter gap={3}>
              <Button ref={cancelRef} onClick={onClose} size='ds-md' variant='ds-ghost'>
                {t('common.cancel')}
              </Button>
              <Button
                onClick={() => {
                  handleDelete(info.id);
                  onClose();
                }}
                size='ds-md'
                variant='ds-danger'
              >
                {t('common.delete')}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Flex>
  );
}

export default ConnectionActionButtons;
